import { Counter } from '../types';

/**
 * Utility function to consistently determine if a URL identifier is an owner URL
 * 
 * This ensures the same logic is used across all components
 * @param counterId The counter identifier from the URL
 * @param counters The array of counters to search
 * @returns True if this is an owner URL, false otherwise
 */
export function isOwnerUrlIdentifier(counterId: string, counters: Counter[]): boolean {
  if (!counterId || !counters || counters.length === 0) {
    return false;
  }
  
  // First try to find by ID
  const counterById = counters.find(c => c._id === counterId);
  
  // If found by ID, it's not an owner URL
  if (counterById) {
    return false;
  }
  
  // If not found by ID, check if it's an owner URL
  const counterByOwnerUrl = counters.find(c => c.ownerUrl === counterId);
  
  // It's an owner URL if we found a counter with this URL as its ownerUrl
  return !!counterByOwnerUrl;
}

/**
 * Utility function to find a counter by either ID or owner URL
 * 
 * @param counterId The counter identifier from the URL
 * @param counters The array of counters to search
 * @returns The counter or undefined if not found
 */
export function findCounterByIdentifier(counterId: string, counters: Counter[]): Counter | undefined {
  if (!counterId || !counters || counters.length === 0) {
    return undefined;
  }
  
  // First try to find by ID
  const counterById = counters.find(c => c._id === counterId);
  
  // If found by ID, return it
  if (counterById) {
    return counterById;
  }
  
  // Otherwise try to find by owner URL
  return counters.find(c => c.ownerUrl === counterId);
}

/**
 * Extract just the ID part from a potential full URL
 * 
 * @param url The URL that might include protocol and domain
 * @returns Just the ID portion of the URL
 */
export function extractIdFromUrl(url: string): string {
  if (!url) return url;
  
  // Handle URLs with protocols
  if (url.includes('://')) {
    const parts = url.split('/');
    return parts[parts.length - 1] || url;
  }
  
  return url;
} 